import { applyPatch, type Operation } from '@st/json-patch'
import { Cause, Effect, Queue, Scope, Stream } from 'effect'
import { queryId } from './topic'
import { PushFailed, QueryClosed } from './types'

/** The one topic a client holds; every query it subscribes to rides on it. */
export const SESSION_TOPIC = 'lq'

/** One message on a joined topic, as the transport delivers it. */
export type TopicEvent = {
  readonly topic: string
  readonly event: string
  readonly payload: unknown
}

/**
 * What the client needs from a transport: join a topic for the lifetime of a
 * scope and deliver its events, send an event on a joined topic and get the
 * server's reply, and say which topics are joined right now. The Phoenix
 * runtime in `@st/sdk` has exactly this shape.
 */
export type QueryTransport = {
  readonly subscribe: (
    topic: string,
    onEvent: (event: TopicEvent) => Effect.Effect<void>
  ) => Effect.Effect<void, never, Scope.Scope>
  readonly push: (topic: string, event: string, payload: unknown) => Effect.Effect<unknown, PushFailed>
  readonly joinedTopics: Stream.Stream<ReadonlyArray<string>>
  /** Leave and rejoin a broken topic, preserving its subscribers. */
  readonly rejoin: (topic: string) => Effect.Effect<void>
}

export type QueryOptions = {
  /** Applied to a loaded value before it is used, for example key casing. */
  readonly decodeValue?: (value: unknown) => unknown
  /** Applied to a patch before it is used, for example key casing in paths. */
  readonly decodePatch?: (patch: ReadonlyArray<Operation>) => ReadonlyArray<Operation>
}

export type QuerySession = {
  /** The answers of one query, starting with the current one. */
  readonly query: <T>(route: string, params: Record<string, unknown>) => Stream.Stream<T, QueryClosed>
}

type Subscribed = { seq: number; value: unknown }

type Diff = {
  from: number
  to: number
  patches: Record<string, ReadonlyArray<Operation>>
  closed: Record<string, string>
}

type Entry = {
  readonly route: string
  readonly params: Record<string, unknown>
  value: unknown
  loaded: boolean
  // Frames that arrived for this query before its subscribe reply. The reply
  // says which sequence its value is current at; frames from there on apply.
  buffered: Array<{ from: number; to: number; patch: ReadonlyArray<Operation> }>
  readonly consumers: Set<Queue.Queue<unknown, QueryClosed>>
}

/**
 * A client's live queries, multiplexed on one topic.
 *
 * Applies the protocol on the client's side. A `subscribe` push answers with
 * the value and the session sequence it is current at. A `diff` frame must
 * continue the sequence and carries one patch per changed query, applied as
 * JSON patches, and the reason for each query the server closed, which fails
 * that query's streams with `QueryClosed`. A gap in the sequence is a defect:
 * the session is broken, and the runtime's rejoin produces fresh subscribes.
 *
 * Two consumers asking the same question share one server subscription; the
 * last one leaving unsubscribes it. When the topic rejoins after a
 * disconnect, every held query is subscribed again and its consumers get the
 * fresh value.
 */
export function querySession(
  transport: QueryTransport,
  options: QueryOptions = {}
): Effect.Effect<QuerySession, never, Scope.Scope> {
  return Effect.gen(function* () {
    // Pushes on behalf of a query are forked here, in the session's own scope:
    // a consumer leaving must not interrupt the subscribe in flight, and the
    // unsubscribe it triggers must not hold its finalizer open for a reply.
    const sessionScope = yield* Effect.scope
    const decodeValue = options.decodeValue ?? ((value: unknown) => value)
    const decodePatch = options.decodePatch ?? ((patch: ReadonlyArray<Operation>) => patch)
    const entries = new Map<string, Entry>()
    let joined = false
    let sessionSeq: number | null = null
    let generation = 0

    const discard = () => {
      generation += 1
      joined = false
      sessionSeq = null
      for (const entry of entries.values()) {
        entry.value = undefined
        entry.loaded = false
        entry.buffered = []
      }
    }

    const emit = (entry: Entry, value: unknown) => {
      entry.value = value
      entry.loaded = true
      for (const consumer of entry.consumers) {
        Queue.offerUnsafe(consumer, value)
      }
    }

    const close = (id: string, entry: Entry, reason: string) => {
      entries.delete(id)
      for (const consumer of entry.consumers) {
        Queue.failCauseUnsafe(consumer, Cause.fail(new QueryClosed(reason)))
      }
    }

    const subscribeOnServer = (id: string, entry: Entry) => {
      const subscribedGeneration = generation
      return transport
        .push(SESSION_TOPIC, 'subscribe', { id, route: entry.route, params: entry.params })
        .pipe(
          Effect.flatMap((reply) =>
            Effect.sync(() => {
              if (generation !== subscribedGeneration || entries.get(id) !== entry) {
                return
              }
              const { seq, value } = reply as Subscribed
              if (sessionSeq == null) {
                sessionSeq = seq
              }
              let current = decodeValue(value)
              for (const frame of entry.buffered) {
                if (frame.from >= seq) {
                  current = applyPatch(current, frame.patch)
                }
              }
              entry.buffered = []
              emit(entry, current)
            })
          ),
          Effect.catchTag('PushFailed', (failure) =>
            Effect.sync(() => {
              if (generation !== subscribedGeneration || entries.get(id) !== entry) {
                return
              }
              if (failure.reason == 'error') {
                const { reason } = failure.response as { reason: string }
                close(id, entry, reason)
              }
              // Otherwise the topic left or rejoined; the rejoin subscribes again.
            })
          )
        )
    }

    const onEvent = (envelope: TopicEvent) =>
      Effect.sync(() => {
        if (!joined || envelope.event != 'diff') {
          return
        }
        const frame = envelope.payload as Diff
        if (sessionSeq != null && frame.from != sessionSeq) {
          throw new Error(
            `[live-query] diff is not contiguous: expected ${sessionSeq}, received ${frame.from}`
          )
        }
        sessionSeq = frame.to

        for (const [id, patch] of Object.entries(frame.patches)) {
          const entry = entries.get(id)
          if (!entry) {
            continue
          }
          const decoded = decodePatch(patch)
          if (!entry.loaded) {
            entry.buffered.push({ from: frame.from, to: frame.to, patch: decoded })
            continue
          }
          emit(entry, applyPatch(entry.value, decoded))
        }

        for (const [id, reason] of Object.entries(frame.closed)) {
          const entry = entries.get(id)
          if (entry) {
            close(id, entry, reason)
          }
        }
      }).pipe(
        // A broken patch chain cannot keep serving its cached values. Invalidate
        // replies already in flight as well as frames queued on the old join.
        // Preserve the defect for the runtime's reporter after requesting rejoin.
        Effect.tapCause(() =>
          Effect.sync(discard).pipe(Effect.andThen(transport.rejoin(SESSION_TOPIC)))
        )
      )

    yield* transport.subscribe(SESSION_TOPIC, onEvent)

    // Every join of the topic, the first and each one after a disconnect,
    // meets a fresh channel: subscribe everything held again.
    yield* Stream.runForEach(transport.joinedTopics, (topics) =>
      Effect.gen(function* () {
        const now = topics.includes(SESSION_TOPIC)
        if (now && !joined) {
          joined = true
          for (const [id, entry] of entries) {
            yield* Effect.forkIn(subscribeOnServer(id, entry), sessionScope)
          }
        }
        if (!now) {
          discard()
        }
      })
    ).pipe(Effect.forkScoped({ startImmediately: true }))

    const query = <T>(route: string, params: Record<string, unknown>): Stream.Stream<T, QueryClosed> =>
      Stream.callback<T, QueryClosed>((queue) =>
        Effect.acquireRelease(
          Effect.gen(function* () {
            const id = queryId(route, params)
            const existing = entries.get(id)
            const entry: Entry = existing ?? {
              route,
              params,
              value: undefined,
              loaded: false,
              buffered: [],
              consumers: new Set()
            }
            entry.consumers.add(queue as Queue.Queue<unknown, QueryClosed>)
            if (entry.loaded) {
              Queue.offerUnsafe(queue, entry.value as T)
            }
            if (!existing) {
              entries.set(id, entry)
              if (joined) {
                yield* Effect.forkIn(subscribeOnServer(id, entry), sessionScope)
              }
            }
            return { id, entry }
          }),
          ({ id, entry }) =>
            Effect.gen(function* () {
              entry.consumers.delete(queue as Queue.Queue<unknown, QueryClosed>)
              if (entry.consumers.size == 0 && entries.get(id) === entry) {
                entries.delete(id)
                if (joined) {
                  yield* Effect.forkIn(
                    transport.push(SESSION_TOPIC, 'unsubscribe', { id }).pipe(Effect.ignore),
                    sessionScope
                  )
                }
              }
            })
        ).pipe(Effect.asVoid)
      )

    return { query }
  })
}
